import posthog from 'posthog-js';
import { PosthogFeatureFlags } from './posthog-feature-flags';

// Event names now live with the tracking catalog in `core/tracking`. Re-exported
// here so existing legacy imports of `PosthogEvents` keep working unchanged.
export { PosthogEvents } from '@/core/tracking';

// The keys live in a leaf module so they can be imported at module scope without dragging this
// one along; re-exported here because every existing call site imports them from `usePostHog`.
export { PosthogFeatureFlags } from './posthog-feature-flags';

/**
 * Evaluated flags, as a reactive mirror of PostHog's own copy.
 *
 * `posthog.getFeatureFlag()` is a plain read: it is not a reactive source, and flags arrive
 * asynchronously some time after `init`. A `computed` that calls it directly is therefore
 * evaluated once against whatever PostHog happened to know at that moment and never again —
 * fine deep inside a funnel where flags loaded long ago, wrong on a landing page a client can
 * deep-link straight into. Mirroring the flags into a `ref` from `onFeatureFlags` gives
 * downstream `computed`s something that actually invalidates when the flags land.
 *
 * An empty mirror means "no flags known" — no PostHog key in the build, or they have not
 * arrived yet. Every flag then reads as off, which keeps a gated surface hidden rather than
 * flashing it at everybody while the build is misconfigured.
 */
const flags = ref<Record<string, string | boolean>>({});

/**
 * Whether PostHog has delivered flags at least once. Lets a surface that must not guess — one
 * that would otherwise flash the default variant and then swap — wait for the real answer.
 * A build without a PostHog key never gets flags, so it counts as loaded straight away.
 */
const flagsLoaded = ref(false);

let subscribed = false;

export function usePostHog() {
    const apiKey: string = import.meta.env.VUE_APP_POSTHOG_PROJECT_ID;

    if (apiKey) {
        posthog.init(apiKey, {
            api_host: 'https://g.firm24.com', // your managed reverse proxy domain
            ui_host: 'https://eu.posthog.com', // necessary because you're using a proxy, this way links will point back to PostHog properly
            defaults: '2026-05-30',
            person_profiles: 'identified_only',
        });

        // `usePostHog` is called from many places; the mirror only needs one subscription.
        if (!subscribed) {
            subscribed = true;
            posthog.onFeatureFlags(() => {
                flags.value = { ...posthog.featureFlags.getFlagVariants() };
                flagsLoaded.value = true;
            });
        }
    }

    return { posthog };
}

/** Reactive like `isFeatureFlagEnabled`: a `computed` reading it re-runs once flags arrive. */
export function areFeatureFlagsLoaded(): boolean {
    return !import.meta.env.VUE_APP_POSTHOG_PROJECT_ID || flagsLoaded.value;
}

/**
 * Truthiness check against the flag mirror.
 *
 * Because it reads the `flags` ref, a reactive scope that calls it — a store's `computed`, a
 * `watch` — tracks the mirror and re-runs when the flags land. Outside such a scope it is a plain
 * one-shot read that returns whatever was known at that moment; wrap it in a `computed` there.
 *
 * Truthiness matches the boolean-flag case. A multivariate flag's variant key is truthy for every
 * variant, so branch on `posthog.getFeatureFlag()` when you need to know which one.
 */
export function isFeatureFlagEnabled(flag: PosthogFeatureFlags): boolean {
    return !!flags.value[flag];
}
